import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import type { ProjectId, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	type IssueLayoutOwner,
	IssueLayoutIssueTypeOwnerType,
} from '../../../common/types/issue/issue-layout-owner.tsx';
import type {
	Availability,
	DisplayConditions,
	FieldProvider,
} from '../../../common/types/layout-items.tsx';

export type ProjectContextType = {
	projectId: ProjectId;
	projectKey: ProjectKey;
	projectType: ProjectType;
};

const getProjectDisplayConditionRestrictions = (
	displayConditions: DisplayConditions,
	projectContext: ProjectContextType,
) => {
	const isHiddenInDisplayConditions = Object.entries(projectContext).some(([key, value]) => {
		if (displayConditions[key]) {
			if (Array.isArray(displayConditions[key])) {
				if (!displayConditions[key].includes(value)) {
					return true;
				}
			} else if (displayConditions[key] !== value) {
				return true;
			}
		}
		return false;
	});

	return { isHiddenInDisplayConditions };
};

const getIssueTypeDisplayConditionRestrictions = (
	displayConditions: DisplayConditions,
	layoutOwners: ReadonlyArray<IssueLayoutOwner>,
) => {
	let isHiddenInDisplayConditions = false;
	const issueLayoutOwnersIssueTypeList = layoutOwners.filter(
		(owner) => owner.type === IssueLayoutIssueTypeOwnerType,
	);
	const filteredIssueTypes = issueLayoutOwnersIssueTypeList
		.filter((owner) =>
			Array.isArray(displayConditions.issueType)
				? !displayConditions.issueType.includes(owner.data.name)
				: owner.data.name !== displayConditions.issueType,
		)
		.map((issueType) => issueType.data.name);

	if (filteredIssueTypes.length || issueLayoutOwnersIssueTypeList.length === 0) {
		isHiddenInDisplayConditions = true;
	}

	const areAllIssueTypesFiltered =
		filteredIssueTypes.length === issueLayoutOwnersIssueTypeList.length;

	return {
		isHiddenInDisplayConditions,
		filteredIssueTypes,
		areAllIssueTypesFiltered,
	};
};

export const getForgeDisplayConditionsRestrictions = (
	availability: Availability | null,
	layoutOwners: ReadonlyArray<IssueLayoutOwner>,
	projectContext: ProjectContextType,
	provider: FieldProvider | undefined,
) => {
	if (!availability || !availability.displayConditions || !provider) return null;
	const { displayConditions } = availability;

	let issueTypeDisplayConditionRestrictions = null;
	let projectDisplayConditionRestrictions = null;
	let shouldDisplayWarning = true;

	if (displayConditions.isSimpleIssueTypeDisplayCondition) {
		issueTypeDisplayConditionRestrictions = getIssueTypeDisplayConditionRestrictions(
			displayConditions,
			layoutOwners,
		);
		if (!issueTypeDisplayConditionRestrictions.isHiddenInDisplayConditions) {
			shouldDisplayWarning = false;
		}
	} else if (displayConditions.isSimpleProjectDisplayCondition) {
		projectDisplayConditionRestrictions = getProjectDisplayConditionRestrictions(
			displayConditions,
			projectContext,
		);
		if (!projectDisplayConditionRestrictions.isHiddenInDisplayConditions) {
			shouldDisplayWarning = false;
		}
	}

	return {
		shouldDisplayWarning,
		issueTypeDisplayConditionRestrictions,
		projectDisplayConditionRestrictions,
		appName: provider.name,
	};
};

export const getAccessNarrowingRestrictedApp = (
	availability: Availability | null,
	provider: FieldProvider | undefined,
) => {
	if (!availability || !availability.isHiddenByAppAccessRules || !provider) return null;

	return provider.name;
};

export const getEcosystemAppsRestrictions = (
	availability: Availability | null,
	layoutOwners: ReadonlyArray<IssueLayoutOwner>,
	projectContext: ProjectContextType,
	provider: FieldProvider | undefined,
) => {
	const forgeDisplayConditionsRestrictions = getForgeDisplayConditionsRestrictions(
		availability,
		layoutOwners,
		projectContext,
		provider,
	);
	const accessNarrowingRestrictedApp = getAccessNarrowingRestrictedApp(availability, provider);

	return {
		forgeDisplayConditionsRestrictions,
		accessNarrowingRestrictedApp,
	};
};
